import {
  performCommercialVisibleChannels,
  projectChannelWithGovernance,
} from '@/constants';
import {
  CollectionMedium,
  ExternalCollectionMedium,
  ExternalSampleType,
  isRendererManagedPdfVersion,
  ReportType,
  SampleType,
} from '@/enums';
import { ChannelName } from '@/enums/channel';
import { ChannelEntity, IWorkflowValue } from '@/interfaces';
import { IExpiredData } from '@/pages/authenPages/Project/ProjectForm/utils';
import {
  INVALID_FIELDS,
  YupNumber,
  YupStringWithoutRequired,
} from '@/validations';
import { t } from 'i18next';
import * as Yup from 'yup';
import { YupString } from '.';

// v7 picks a renderer template (id); other versions use pdfTemplateName only.
const rendererPdfTemplateIdSchema = YupStringWithoutRequired.nullable().when(
  ['outputTypes', 'pdfGeneratorVersion'],
  {
    is: (types: string[], version?: string) =>
      types?.includes(ReportType.PDF) && isRendererManagedPdfVersion(version),
    then: () => YupString,
  },
);

const stringRequiredIfAmiliHealth = (channels: ChannelEntity[]) =>
  YupStringWithoutRequired.nullable().when('channelId', {
    is: (value: string) =>
      channels?.find(ch => ch.id === value)?.name === ChannelName.AMILI_HEALTH,
    then: () => YupString.required(INVALID_FIELDS.REQUIRED),
  });

const emailsSchema = Yup.string().test(
  'emails',
  INVALID_FIELDS.EMAIL,
  (value: string | undefined) => {
    if (!value) return true;
    const emails = value
      .split(',')
      .map(e => e.trim())
      .filter(Boolean);
    if (emails.length === 0) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.every(email => emailRegex.test(email));
  },
);

const analysisBucketPathPattern =
  /^s3:\/\/((?!\d+\.\d+\.\d+\.\d+$)(?!-)(?!.*--)(?!.*(xn--|sthree-|amzn-s3-demo-))[a-z0-9]([a-z0-9\-.]{1,61}[a-z0-9])(?<!-s3alias|--ol-s3|\.mrap|--x-s3|--table-s3))\/([\w@:+.-]+(\/[\w@:+.-]+)*)$/;
export const createProjectSchema = (
  channels: ChannelEntity[],
  currentChannel?: ChannelName,
) => {
  const allowGovernanceInput =
    currentChannel && projectChannelWithGovernance.includes(currentChannel);

  return Yup.object().shape({
    name: YupString,
    projectCode: YupStringWithoutRequired.nullable(),
    amiliPicUserId: stringRequiredIfAmiliHealth(channels),
    channelId: YupString,
    tenantIds: Yup.array().of(YupStringWithoutRequired.nullable()),
    /* ----------------------------- Data Governance ---------------------------- */
    ownership: allowGovernanceInput
      ? YupString
      : YupStringWithoutRequired.nullable(),
    expiry: allowGovernanceInput
      ? Yup.string()
          .nullable()
          .when('expiredData', {
            is: (expiredData: IExpiredData) =>
              !expiredData.isExpired && !expiredData.isNoneExpired,
            then: () => Yup.string().required(INVALID_FIELDS.REQUIRED),
          })
      : YupStringWithoutRequired.nullable(),
    policy: allowGovernanceInput
      ? YupString
      : YupStringWithoutRequired.nullable(),
    declarer: allowGovernanceInput
      ? YupString
      : YupStringWithoutRequired.nullable(),
    /* -------------------------------------------------------------------------- */
    address: Yup.object().nullable().shape({
      emails: emailsSchema,
    }),
    // .when('emailD4', {
    //   is: true,
    //   then: () =>
    //     Yup.object({
    //       emails: emailsSchema.required(INVALID_FIELDS.REQUIRED),
    //     }),
    // })
    // .when('emailD2D31', {
    //   is: true,
    //   then: () =>
    //     Yup.object({
    //       emails: emailsSchema.required(INVALID_FIELDS.REQUIRED),
    //     }),
    // }),
    workflow: Yup.object()
      .nullable()
      .shape({
        orgName: YupString,
        PIName: YupString,
        sampleType: Yup.array()
          .min(1, INVALID_FIELDS.MIN_SAMPLE_TYPE)
          .of(Yup.string()),
        ffq: YupString,
        reibursement: YupString,
        collectionMedium: Yup.array()
          .min(1, INVALID_FIELDS.MIN_COLLECTION)
          .of(Yup.string()),
        qualtricsSurveyId: YupStringWithoutRequired.nullable(),
      })
      .when('channelId', {
        is: (value: string) =>
          [ChannelName.EXTERNAL, ChannelName.AMILI_HEALTH].includes(
            channels?.find(ch => ch.id === value)?.name as ChannelName,
          ),
        then: schema =>
          schema.shape({
            orgName: YupStringWithoutRequired.nullable(),
            PIName: YupStringWithoutRequired.nullable(),
            ffq: YupStringWithoutRequired.nullable(),
            reibursement: YupStringWithoutRequired.nullable(),
          }),
      }),
    sequencingTypes: Yup.array()
      .of(YupString)
      .min(1, INVALID_FIELDS.MIN_SELECTION),
    sixteenSPipeline: Yup.object().shape({
      outputTypes: Yup.array()
        .of(YupString)
        .min(1, INVALID_FIELDS.MIN_OUTPUT_TYPE),
      pdfTemplateLanguages: Yup.array()
        .of(YupString)
        .when('outputTypes', {
          is: (types: string[]) => types?.includes(ReportType.PDF),
          then: schema =>
            schema
              .required(INVALID_FIELDS.REQUIRED)
              .min(1, INVALID_FIELDS.REQUIRED),
          otherwise: schema => schema.nullable(),
        }),
      pdfGeneratorVersion: YupStringWithoutRequired.nullable().when(
        'outputTypes',
        {
          is: (types: string[]) => types?.includes(ReportType.PDF),
          then: () => YupString,
        },
      ),
      foodSetName: YupStringWithoutRequired.nullable().when('outputTypes', {
        is: (types: string[]) => types?.includes(ReportType.PDF),
        then: () => YupString,
      }),
      pdfTemplateName: YupStringWithoutRequired.nullable().when('outputTypes', {
        is: (types: string[]) => types?.includes(ReportType.PDF),
        then: () => YupString,
      }),
      pdfTemplateId: rendererPdfTemplateIdSchema,
    }),
    noOfAutoGenSamples: YupNumber.min(1, ({ min }) =>
      t(INVALID_FIELDS.MIN_NUMBER, { number: min }),
    ).when('workflow', {
      is: (workflow: IWorkflowValue) => {
        const isGeneratedSamples =
          workflow?.sampleType?.includes(SampleType.STOOL) &&
          workflow?.collectionMedium?.includes(CollectionMedium.ZYMO);
        return isGeneratedSamples;
      },
      then: schema => schema.required(INVALID_FIELDS.REQUIRED),
    }),
    isPerformAnalysisBatch: Yup.boolean().optional(),
    analysisBucketPath: YupStringWithoutRequired.nullable()
      .optional()
      .trim()
      .matches(
        analysisBucketPathPattern,
        'Invalid pattern. Please follow the tooltip icon for details.',
      )
      .when(['channelId', 'isPerformAnalysisBatch'], {
        is: (channelId: string, isPerformAnalysisBatch: boolean) => {
          const findChannelNameById =
            channels.find(({ id }) => id === channelId)?.name ?? '';
          return (
            performCommercialVisibleChannels.includes(
              findChannelNameById as ChannelName,
            ) && isPerformAnalysisBatch
          );
        },
        then: () => Yup.string().required(INVALID_FIELDS.REQUIRED),
      }),
    deliveryAddress: stringRequiredIfAmiliHealth(channels),
    clientPriority: YupStringWithoutRequired.nullable(),
    country: stringRequiredIfAmiliHealth(channels),
    currency: YupStringWithoutRequired.nullable(),
  });
};

export const externalProjectSchema = Yup.object().shape({
  name: YupString,
  channelId: YupString,
  externalPipeline: YupStringWithoutRequired.nullable(),
  externalSampleType: YupString,
  externalCollectionMedium: Yup.array()
    .of(YupStringWithoutRequired)
    .when('externalSampleType', {
      is: ExternalSampleType.STOOL,
      then: schema => schema.min(1, INVALID_FIELDS.MIN_COLLECTION),
      otherwise: schema => schema.nullable(),
    }),
  externalSequencingTypeId: YupStringWithoutRequired.nullable(),
  externalHaveSentB1: Yup.boolean().nullable(),
  externalHaveSentB2: Yup.boolean().nullable(),
  tenantIds: Yup.array().of(YupStringWithoutRequired.nullable()),
  externalEmail: Yup.string()
    .nullable()
    .when('externalHaveSentB1', {
      is: true,
      then: () => Yup.string().required(INVALID_FIELDS.REQUIRED),
    })
    .when('externalHaveSentB2', {
      is: true,
      then: () => Yup.string().required(INVALID_FIELDS.REQUIRED),
    })
    .email(INVALID_FIELDS.EMAIL),
  externalSpeciesType: Yup.string()
    .nullable()
    .when('externalSampleType', {
      is: ExternalSampleType.FASTQ,
      otherwise: () => Yup.string().required(INVALID_FIELDS.REQUIRED),
    }),
  externalTissueOrgan: Yup.string()
    .nullable()
    .when('externalSampleType', {
      is: ExternalSampleType.FASTQ,
      otherwise: () => Yup.string().required(INVALID_FIELDS.REQUIRED),
    }),
  externalElutionBuffer: Yup.string()
    .nullable()
    .when('externalSampleType', {
      is: ExternalSampleType.FASTQ,
      otherwise: () => Yup.string().required(INVALID_FIELDS.REQUIRED),
    }),
  ownership: YupString,
  expiry: Yup.string()
    .nullable()
    .when('expiredData', {
      is: (expiredData: IExpiredData) =>
        !expiredData.isExpired && !expiredData.isNoneExpired,
      then: () => Yup.string().required(INVALID_FIELDS.REQUIRED),
    }),
  policy: YupString,
  declarer: YupString,
  sequencingTypes: Yup.array()
    .of(YupString)
    .min(1, INVALID_FIELDS.MIN_SELECTION),
  sixteenSPipeline: Yup.object().shape({
    outputTypes: Yup.array()
      .of(YupString)
      .min(1, INVALID_FIELDS.MIN_OUTPUT_TYPE),
    pdfTemplateLanguages: Yup.array()
      .of(YupString)
      .when('outputTypes', {
        is: (types: string[]) => types?.includes(ReportType.PDF),
        then: schema =>
          schema
            .required(INVALID_FIELDS.REQUIRED)
            .min(1, INVALID_FIELDS.REQUIRED),
        otherwise: schema => schema.nullable(),
      }),
    pdfGeneratorVersion: YupStringWithoutRequired.nullable().when(
      'outputTypes',
      {
        is: (types: string[]) => types?.includes(ReportType.PDF),
        then: () => YupString,
      },
    ),
    foodSetName: YupStringWithoutRequired.nullable().when('outputTypes', {
      is: (types: string[]) => types?.includes(ReportType.PDF),
      then: () => YupString,
    }),
    pdfTemplateName: YupStringWithoutRequired.nullable().when('outputTypes', {
      is: (types: string[]) => types?.includes(ReportType.PDF),
      then: () => YupString,
    }),
    pdfTemplateId: rendererPdfTemplateIdSchema,
  }),
  noOfAutoGenSamples: Yup.number()
    .nullable()
    .when(['externalSampleType', 'externalCollectionMedium'], {
      is: (
        externalSampleType: ExternalSampleType,
        externalCollectionMedium: ExternalCollectionMedium[],
      ) => {
        const isGeneratedSamples =
          externalSampleType === ExternalSampleType.STOOL &&
          externalCollectionMedium?.includes(ExternalCollectionMedium.ZYMO);
        return isGeneratedSamples;
      },
      then: () =>
        Yup.number()
          .positive()
          .min(1, ({ min }) => t(INVALID_FIELDS.MIN_NUMBER, { number: min }))
          .required(INVALID_FIELDS.REQUIRED),
    }),
});
